/**
 * Role-based access — the permission catalogue, the starter roles, and the one
 * rule that combines them (STORY-160, decisions RB1–RB8).
 *
 * **Permissions are code; roles are data.** A permission is a code path: the
 * route or service check that asks for it ships in the same release as the key,
 * which is why the catalogue is a file and not a table. Which roles hold which
 * permissions is each clinic's own business and lives in its database rows;
 * `STARTER_ROLES` is only what a clinic is offered, copied into its own rows
 * when accepted — TP10's starter-library pattern — and never read at runtime to
 * decide anything.
 *
 * **This file has no imports, on purpose.** The API, the web app, the worked
 * examples in `docs/rbac/prototype/examples.ts` and the access explorer all run
 * it — the last two straight from source under Node's type stripping — so it
 * must stay erasable TypeScript with nothing to resolve. One copy, four readers.
 *
 * `guards` names the routes each permission protects, so a reader can go from
 * the matrix to the code. A route marked *(service)* is checked inside the
 * handler, because the route alone cannot tell — cancelling is a status change,
 * a price override is a line that carries an amount.
 */

export type ModuleKey =
  | "home"
  | "calendar"
  | "patients"
  | "clinical"
  | "treatment"
  | "billing"
  | "accounting"
  | "staff"
  | "inventory"
  | "settings"
  | "access"
  | "tasks";

export interface ModuleDef {
  key: ModuleKey;
  label: string;
  summary: string;
  /**
   * A core module is part of every subscription. A non-core one can be left
   * out of a clinic's plan, and then nobody in that clinic holds its
   * permissions whatever their role says (the deck's *Licenses*, slide 13).
   */
  core: boolean;
}

export const MODULES: readonly ModuleDef[] = [
  { key: "home", label: "Home", summary: "Today's dashboard", core: true },
  {
    key: "calendar",
    label: "Calendar",
    summary: "Bookings, chairs, waitlist, agent bookings",
    core: true,
  },
  {
    key: "patients",
    label: "Patients",
    summary: "The file: identity, contacts, flags",
    core: true,
  },
  { key: "clinical", label: "Clinical record", summary: "Chart, notes, imaging", core: true },
  {
    key: "treatment",
    label: "Treatment plans",
    summary: "The procedure list and plans",
    core: true,
  },
  {
    key: "billing",
    label: "Billing",
    summary: "Invoices, payments, balances — the financial module",
    core: false,
  },
  {
    key: "accounting",
    label: "Accounting",
    summary: "Profit, expenses and salaries",
    core: false,
  },
  { key: "staff", label: "Staff", summary: "Rota, day exceptions, time off, leave", core: true },
  {
    key: "inventory",
    label: "Inventory",
    summary: "Stock, movements, bills of materials",
    core: false,
  },
  {
    key: "settings",
    label: "Settings",
    summary: "Clinic, locations, chairs, integrations",
    core: true,
  },
  {
    key: "access",
    label: "People & access",
    summary: "Staff accounts, roles, overrides",
    core: true,
  },
  // Base plan (TK-D20). Seeing your own tasks and adding one needs no
  // permission (TK-D24); these two are for everyone's, and for the settings.
  { key: "tasks", label: "Tasks", summary: "The team's jobs, manual and automatic", core: true },
];

/**
 * - `standard` — a normal part of somebody's job.
 * - `sensitive` — money, deletion, or another person's data. May be granted by
 *   override, but the override must carry a reason and is listed in the
 *   overrides report.
 * - `critical` — reaches access itself, so it is an escalation path. **Only a
 *   role can carry it; no override may grant or revoke it.**
 */
export type Risk = "standard" | "sensitive" | "critical";

/**
 * How the code enforces it at `1bb89bb`, so the migration can prove it changed
 * nothing: `open` is any signed-in member (working plan §3.3), `admin` is
 * `@Roles("ADMIN")`, `new` is a check that does not exist yet.
 */
export type Today = "open" | "admin" | "new";

export interface PermissionDef {
  module: ModuleKey;
  /** What an administrator reads in the matrix. A sentence, not a key. */
  label: string;
  risk: Risk;
  today: Today;
  /** Holding this means holding these too — issuing an invoice is useless without seeing one. */
  implies?: readonly string[];
  /**
   * **The safety floor.** Every member holds it whatever their roles, and no
   * override may revoke it. Only allergy and medical flags qualify: anybody who
   * books or treats a patient must be able to see that they are allergic.
   */
  floor?: true;
  guards: readonly string[];
}

export const PERMISSIONS = {
  // ── Home ────────────────────────────────────────────────────────────────
  "home.view": {
    module: "home",
    label: "Open Home: today's patients, alerts, waitlist offers",
    risk: "standard",
    today: "open",
    guards: [
      "GET /home/attendance",
      "GET /home/no-show-risk",
      "GET /home/figures (money fields need billing.view)",
      "GET /home/waitlist/:entryId/offers",
      "GET /clinic-alerts",
      "POST /clinic-alerts/:alertId/resolve",
    ],
  },

  // ── Calendar ────────────────────────────────────────────────────────────
  "calendar.view": {
    module: "calendar",
    label: "See the calendar and every appointment on it",
    risk: "standard",
    today: "open",
    guards: [
      "GET /appointments, /week, /month, /providers, /external-blocks",
      "GET /appointments/:id/slots, /series, /status-events, /prep-list",
      "GET /chair-assignments",
      "GET /booking-requests/waiting (the sidebar's count)",
    ],
  },
  // CF-D8 (2026-10-21): how much is seen at once, never what is seen — each
  // location still answers with the roles held there (CF-D9).
  "calendar.across_locations": {
    module: "calendar",
    label: "See several locations in one calendar",
    risk: "standard",
    today: "new",
    implies: ["calendar.view"],
    guards: ["GET /calendar/combined", "GET /calendar/combined/month"],
  },
  "appointments.book": {
    module: "calendar",
    label: "Book appointments, series and planned visits",
    risk: "standard",
    today: "open",
    implies: ["calendar.view"],
    guards: [
      "POST /appointments",
      "PATCH /appointments/:appointmentId (QA4-D3: procedure, teeth, provider, description)",
      "POST /appointments/series",
      "POST /plans/chain-proposal, /plans/:planId/book-chain",
      "POST /plans/:planId/parts/:partId/book",
    ],
  },
  "appointments.move": {
    module: "calendar",
    label: "Move appointments, including a whole series or chain",
    risk: "standard",
    today: "open",
    implies: ["calendar.view"],
    guards: [
      "PATCH /appointments/:id/move",
      "PATCH /appointments/:id/series/move",
      "POST /plans/:planId/shift-chain",
    ],
  },
  "appointments.status": {
    module: "calendar",
    label: "Check in, seat, complete, or mark a no-show",
    risk: "standard",
    today: "open",
    implies: ["calendar.view"],
    guards: [
      "PATCH /appointments/:id/status (any target but CANCELLED)",
      "POST /appointments/:id/complete",
    ],
  },
  "appointments.cancel": {
    module: "calendar",
    label: "Cancel an appointment (Q5)",
    risk: "standard",
    today: "open",
    implies: ["calendar.view"],
    guards: [
      "PATCH /appointments/:id/status → CANCELLED (service)",
      "POST /plans/:planId/cancel-later",
    ],
  },
  "appointments.validate": {
    module: "calendar",
    label: "Approve or decline what the WhatsApp agent booked or requested",
    risk: "standard",
    today: "open",
    implies: ["calendar.view"],
    guards: [
      "GET /appointments/awaiting-validation",
      "POST /appointments/:id/validate, /decline",
      "GET /booking-requests",
      "POST /booking-requests/:id/approve, /decline",
    ],
  },
  "calendar.roster": {
    module: "calendar",
    label: "Put providers in chairs for the day",
    risk: "standard",
    today: "open",
    implies: ["calendar.view"],
    guards: ["POST /chair-assignments", "DELETE /chair-assignments/:id"],
  },
  "waitlist.manage": {
    module: "calendar",
    label: "Add to, edit and book from the waitlist",
    risk: "standard",
    today: "open",
    implies: ["calendar.view"],
    guards: [
      "GET /waitlist, /waitlist/matches",
      "POST /waitlist, /waitlist/:id/book, /waitlist/:id/cancel",
      "PATCH /waitlist/:id",
    ],
  },
  "reminders.send": {
    module: "calendar",
    label: "Send a patient a reminder on WhatsApp",
    risk: "standard",
    today: "open",
    implies: ["calendar.view"],
    guards: ["POST /appointments/:id/remind"],
  },

  // ── Patients ────────────────────────────────────────────────────────────
  "patients.view": {
    module: "patients",
    label: "Find patients and open their file",
    risk: "standard",
    today: "open",
    guards: ["GET /patients", "GET /patients/:id", "GET /patients/insurers"],
  },
  "patients.create": {
    module: "patients",
    label: "Open a new patient file",
    risk: "standard",
    today: "open",
    implies: ["patients.view"],
    guards: ["POST /patients"],
  },
  "patients.edit": {
    module: "patients",
    label: "Change a patient's details, contacts and insurer",
    risk: "standard",
    today: "open",
    implies: ["patients.view"],
    guards: ["PATCH /patients/:id"],
  },
  "patients.delete": {
    module: "patients",
    label: "Delete or restore a patient file",
    risk: "sensitive",
    today: "admin",
    implies: ["patients.view"],
    guards: ["DELETE /patients/:id", "POST /patients/:id/restore"],
  },
  "patients.flags.view": {
    module: "patients",
    label: "See allergy and medical flags",
    risk: "standard",
    today: "open",
    floor: true,
    guards: ["GET /patients/:patientId/flags"],
  },
  "patients.flags.manage": {
    module: "patients",
    label: "Add, change, resolve and reopen flags",
    risk: "standard",
    today: "open",
    implies: ["patients.flags.view"],
    guards: [
      "POST /patients/:patientId/flags",
      "PATCH /patients/:patientId/flags/:flagId",
      "POST /patients/:patientId/flags/:flagId/resolve, /reopen",
    ],
  },

  // ── Clinical record ─────────────────────────────────────────────────────
  "clinical.chart.view": {
    module: "clinical",
    label: "See the tooth chart and visit history",
    risk: "standard",
    today: "open",
    implies: ["patients.view"],
    guards: [
      "GET /patients/:patientId/teeth, /teeth/procedures, /teeth/:fdi",
      "GET /patients/:patientId/visits, /visits/:date",
    ],
  },
  "clinical.chart.edit": {
    module: "clinical",
    label: "Record work on the chart",
    risk: "standard",
    today: "open",
    implies: ["clinical.chart.view"],
    guards: [
      "POST /patients/:patientId/teeth/:fdi/procedures, /removed, /normal",
      "DELETE /patients/:patientId/teeth/:fdi/procedures/:procedureId",
      "PUT /patients/:patientId/teeth/primary-teeth",
    ],
  },
  "clinical.notes.view": {
    module: "clinical",
    label: "Read clinical notes",
    risk: "standard",
    today: "open",
    implies: ["patients.view"],
    guards: ["GET /patients/:patientId/notes", "GET /patients/:patientId/notes/:noteId/revisions"],
  },
  "clinical.notes.write": {
    module: "clinical",
    label: "Write and revise clinical notes",
    risk: "standard",
    today: "open",
    implies: ["clinical.notes.view"],
    guards: ["POST /patients/:patientId/notes", "PATCH /patients/:patientId/notes/:noteId"],
  },
  "clinical.notes.hide": {
    module: "clinical",
    label: "Hide a clinical note (recoverable, P2 Q12)",
    risk: "sensitive",
    today: "open",
    implies: ["clinical.notes.view"],
    guards: ["DELETE /patients/:patientId/notes/:noteId"],
  },
  "clinical.imaging.view": {
    module: "clinical",
    label: "See X-rays, photos and documents",
    risk: "standard",
    today: "open",
    implies: ["patients.view"],
    guards: ["GET /patients/:patientId/attachments"],
  },
  "clinical.imaging.upload": {
    module: "clinical",
    label: "Upload X-rays, photos and documents",
    risk: "standard",
    today: "open",
    implies: ["clinical.imaging.view"],
    guards: [
      "POST /patients/:patientId/attachments/presign",
      "POST /patients/:patientId/attachments",
    ],
  },
  "clinical.imaging.delete": {
    module: "clinical",
    label: "Delete an image or document",
    risk: "sensitive",
    today: "open",
    implies: ["clinical.imaging.view"],
    guards: ["DELETE /patients/:patientId/attachments/:attachmentId"],
  },

  // ── Treatment plans ─────────────────────────────────────────────────────
  "procedures.view": {
    module: "treatment",
    label: "See the procedure list and its prices",
    risk: "standard",
    today: "open",
    guards: ["GET /procedure-codes", "GET /procedure-codes/:codeId/materials"],
  },
  "procedures.manage": {
    module: "treatment",
    label: "Change procedures, prices, visits and materials",
    risk: "sensitive",
    today: "admin",
    implies: ["procedures.view"],
    guards: [
      "POST /procedure-codes, /starter-library, /copy-from, /import",
      "PATCH /procedure-codes/:codeId",
      "PUT /procedure-codes/:codeId/visits",
      "POST, PATCH, DELETE /procedure-codes/:codeId/materials",
    ],
  },
  "plans.view": {
    module: "treatment",
    label: "See a patient's plans and what is still to book",
    risk: "standard",
    today: "open",
    implies: ["patients.view"],
    guards: ["GET /plans", "GET /plans/waiting", "GET /plans/:planId"],
  },
  "plans.edit": {
    module: "treatment",
    label: "Change a patient's plan: visits, items, completion",
    risk: "standard",
    today: "open",
    implies: ["plans.view"],
    guards: ["POST, PATCH, DELETE /plans/:planId/** (the 20 remaining plan routes)"],
  },

  // ── Billing — the financial module ──────────────────────────────────────
  "billing.view": {
    module: "billing",
    label: "See invoices, payments and what patients owe",
    risk: "sensitive",
    today: "open",
    implies: ["patients.view"],
    guards: [
      "GET /patients/:patientId/invoices",
      "GET /home/figures — the outstanding-money fields (serializer)",
    ],
  },
  "billing.invoice.create": {
    module: "billing",
    label: "Raise invoices and add lines at catalogue price",
    risk: "standard",
    today: "open",
    implies: ["billing.view"],
    guards: [
      "POST /patients/:patientId/invoices",
      "POST /patients/:patientId/invoices/visit-work",
      "POST /patients/:patientId/invoices/:invoiceId/lines (no amount)",
    ],
  },
  "billing.invoice.issue": {
    module: "billing",
    label: "Issue an invoice",
    risk: "standard",
    today: "open",
    implies: ["billing.view"],
    guards: ["POST /patients/:patientId/invoices/:invoiceId/issue"],
  },
  "billing.invoice.void": {
    module: "billing",
    label: "Void an issued invoice",
    risk: "sensitive",
    today: "open",
    implies: ["billing.view"],
    guards: ["POST /patients/:patientId/invoices/:invoiceId/void"],
  },
  "billing.price.override": {
    module: "billing",
    label: "Charge a price other than the catalogue's (ADR-0003)",
    risk: "sensitive",
    today: "open",
    implies: ["billing.invoice.create"],
    guards: ["POST /patients/:patientId/invoices/:invoiceId/lines with an amount (service)"],
  },
  "billing.payment.record": {
    module: "billing",
    label: "Record a payment",
    risk: "sensitive",
    today: "open",
    implies: ["billing.view"],
    guards: ["POST /patients/:patientId/invoices/:invoiceId/payments"],
  },
  "billing.reports.view": {
    module: "billing",
    label: "See takings and money-owed reports",
    risk: "sensitive",
    today: "new",
    implies: ["billing.view"],
    guards: ["(no route yet — the deck's Invoicing & Accounting module)"],
  },

  // ── Accounting (STORY-172) ──────────────────────────────────────────────
  // Its own block rather than `billing.reports.view`, which Practice manager
  // also holds and an external accountant might: salaries are here, which
  // AC-D13 shows to Admin and Finance officer only (AC-D15).
  //
  // **Five, not two, since QA4-D8** (2026-10-18): profit, expenses and salaries
  // are seen separately, and expenses and salaries recorded separately. The two
  // old keys keep their names and narrow — `accounting.view` is the profit
  // (every cost figure elsewhere, QF-D1 and QF-D20, already meant that) and
  // `accounting.manage` the expenses — so nothing that checked them changes
  // meaning; migration `20261018160000_accounting_split` gave each holder the
  // new ones, so nobody gained or lost access on the day.
  "accounting.view": {
    module: "accounting",
    label: "See profit",
    risk: "sensitive",
    today: "new",
    guards: [
      "GET /accounting/year, /today, /team, /years, /targets",
      "Tray cost, price per use and profit wherever they appear (QF-D1, QF-D20)",
    ],
  },
  "accounting.expenses.view": {
    module: "accounting",
    label: "See expenses",
    risk: "sensitive",
    today: "new",
    guards: ["GET /accounting/expenses, /accounting/expenses/:id/history, /accounting/categories"],
  },
  "accounting.salaries.view": {
    module: "accounting",
    label: "See everyone's salary",
    risk: "sensitive",
    today: "new",
    guards: ["GET /accounting/salaries"],
  },
  "accounting.manage": {
    module: "accounting",
    label: "Record expenses",
    risk: "sensitive",
    today: "new",
    implies: ["accounting.expenses.view"],
    guards: [
      "POST, PATCH, DELETE /accounting/expenses/**",
      "POST, PATCH, DELETE /accounting/categories/**",
      "PUT /accounting/targets",
    ],
  },
  "accounting.salaries.manage": {
    module: "accounting",
    label: "Set salaries",
    risk: "sensitive",
    today: "new",
    implies: ["accounting.salaries.view"],
    guards: ["POST /accounting/salaries/:userId, PUT /accounting/salaries/:userId/last-day"],
  },

  // ── Staff ───────────────────────────────────────────────────────────────
  "staff.view": {
    module: "staff",
    label: "See the team and the published rota",
    risk: "standard",
    today: "open",
    guards: ["GET /staff/team", "GET /staff/week", "GET /staff/:userId/week, /pattern"],
  },
  "staff.rota.edit.own": {
    module: "staff",
    label: "Change your own repeating week and day exceptions",
    risk: "standard",
    today: "open",
    implies: ["staff.view"],
    guards: ["PUT, POST, DELETE /staff/:userId/** where :userId is the caller (service)"],
  },
  "staff.rota.edit.any": {
    module: "staff",
    label: "Change anybody's repeating week and day exceptions (Q28)",
    risk: "standard",
    today: "open",
    implies: ["staff.rota.edit.own"],
    guards: ["PUT, POST, DELETE /staff/:userId/** for anybody (service)"],
  },
  "staff.rota.publish": {
    module: "staff",
    label: "Publish or unpublish a rota week",
    risk: "standard",
    today: "admin",
    implies: ["staff.view"],
    guards: ["POST /staff/week/publish", "DELETE /staff/week/publish"],
  },
  "staff.timeoff.request": {
    module: "staff",
    label: "Ask for time off, and see your own balance",
    risk: "standard",
    today: "open",
    guards: ["POST /time-off", "GET /time-off (own)", "GET /time-off/balances (own)"],
  },
  "staff.timeoff.decide": {
    module: "staff",
    label: "Approve or decline others' time off — never your own (S8, Q28)",
    risk: "standard",
    today: "open",
    implies: ["staff.leave.view.any"],
    guards: ["POST /time-off/:id/approve, /decline, /reopen", "GET /time-off/:id/coverage"],
  },
  "staff.leave.view.any": {
    module: "staff",
    label: "See everyone's time off and leave balances",
    risk: "sensitive",
    today: "open",
    implies: ["staff.view"],
    guards: ["GET /time-off (all)", "GET /time-off/balances (all)"],
  },

  // ── HR, inside Staff (STORY-181, HR-D1–HR-D9) ───────────────────────────
  // Staff's module rather than one of its own: HR is tabs of Staff (HR-D1).
  "hr.self": {
    module: "staff",
    label: "See your own contract and certificates, and upload a renewal",
    risk: "standard",
    today: "new",
    floor: true,
    guards: [
      "GET /hr/me, GET /hr/settings",
      "POST /hr/certificates/:id/renewals, /renewals/presign (own, waits for a check)",
      "GET /hr/renewals/:id/file (own)",
      "POST /time-off/:id/withdraw (own, waiting)",
    ],
  },
  "hr.team.view": {
    module: "staff",
    label: "See the team's contracts, leave and certificates",
    risk: "sensitive",
    today: "new",
    implies: ["staff.view"],
    guards: ["GET /hr/team", "GET /hr/people/:userId"],
  },
  "hr.record.manage": {
    module: "staff",
    label: "Change somebody's contract: type, probation and notice",
    risk: "sensitive",
    today: "new",
    implies: ["hr.team.view"],
    guards: ["PATCH /hr/people/:userId/contract"],
  },
  "hr.compliance.view": {
    module: "staff",
    label: "See everyone's certificates and their documents",
    risk: "sensitive",
    today: "new",
    implies: ["hr.team.view"],
    guards: ["GET /hr/certificates", "GET /hr/renewals/:id/file (anybody's)"],
  },
  "hr.compliance.manage": {
    module: "staff",
    label: "Add, renew and remove certificates, and check an uploaded renewal",
    risk: "sensitive",
    today: "new",
    implies: ["hr.compliance.view"],
    guards: [
      "POST /hr/people/:userId/certificates",
      "PATCH, DELETE /hr/certificates/:id",
      "POST /hr/certificates/:id/renewals, /renewals/presign (anybody's, counts at once)",
      "POST /hr/renewals/:id/check, /reject",
    ],
  },
  "hr.absence.record": {
    module: "staff",
    label: "Record somebody off sick today, and back at work (HR-D8)",
    risk: "sensitive",
    today: "new",
    implies: ["hr.team.view"],
    guards: ["POST /hr/people/:userId/sick", "DELETE /hr/people/:userId/sick"],
  },

  // ── Inventory ───────────────────────────────────────────────────────────
  "inventory.view": {
    module: "inventory",
    label: "See stock, its history, suppliers and orders",
    risk: "standard",
    today: "open",
    guards: [
      "GET /inventory",
      "GET /inventory/:itemId/movements",
      "GET /suppliers",
      "GET /suppliers/reorder",
      "GET /supplier-orders",
    ],
  },
  "inventory.movement.record": {
    module: "inventory",
    label: "Adjust a count, mark a unit opened, order and receive stock (TP17, SU-D8)",
    risk: "standard",
    today: "open",
    implies: ["inventory.view"],
    guards: [
      "POST /inventory/:itemId/adjust",
      "POST /inventory/:itemId/open",
      "POST /supplier-orders",
      "POST /supplier-orders/:orderId/receive",
      "POST /supplier-orders/:orderId/cancel",
    ],
  },
  "inventory.items.manage": {
    module: "inventory",
    label: "Add and change stock items, suppliers and their prices",
    risk: "standard",
    today: "admin",
    implies: ["inventory.view"],
    guards: [
      "POST /inventory",
      "PATCH /inventory/:itemId",
      "POST /suppliers",
      "PATCH /suppliers/:supplierId",
      "POST /suppliers/:supplierId/prices",
      "POST /suppliers/:supplierId/import",
      "PATCH /supplier-prices/:priceId",
      "DELETE /supplier-prices/:priceId",
    ],
  },

  // ── Tasks (STORY-198, TK-D15, TK-D24) ───────────────────────────────────
  // Your own tasks and your roles' unclaimed ones need nothing (`@Member`):
  // the list is decided inside the handler, by whose task it is.
  "tasks.view_all": {
    module: "tasks",
    label: "See everyone's tasks, not only your own and your role's",
    risk: "standard",
    today: "new",
    guards: ["GET /tasks?view=all, and any task at a location they reach (checked in the handler)"],
  },
  "tasks.manage": {
    module: "tasks",
    label: "Set up task types and automatic tasks",
    risk: "standard",
    today: "new",
    guards: [
      "POST /task-types",
      "PATCH /task-types/:typeId",
      "GET /task-rules",
      "PUT /task-rules/:kind",
    ],
  },

  // ── Settings ────────────────────────────────────────────────────────────
  "settings.clinic.manage": {
    module: "settings",
    label: "Change the clinic's details, branding and setup",
    risk: "sensitive",
    today: "admin",
    guards: [
      "GET, PATCH /clinic",
      "PATCH /hr/settings (the HR warning window)",
      "POST /clinic/logo/presign, /clinic/logo; DELETE /clinic/logo",
      "GET /setup-state",
    ],
  },
  "settings.locations.manage": {
    module: "settings",
    label: "Open and change locations, hours, chairs and block reasons",
    risk: "sensitive",
    today: "admin",
    guards: [
      "POST /branches; PATCH /branches/:id; PUT /branches/:id/hours",
      "POST /chairs; PATCH /chairs/:id",
      "POST, PATCH, DELETE /block-reasons; POST /block-reasons/starter-set",
    ],
  },
  "settings.integrations.manage": {
    module: "settings",
    label: "Connect Google Calendar and pair chairs",
    risk: "sensitive",
    today: "admin",
    guards: [
      "GET /google/settings",
      "POST /google/address",
      "POST /google/chairs/:chairId/pair, /confirm, /unpair",
    ],
  },

  // ── People & access ─────────────────────────────────────────────────────
  "people.view": {
    module: "access",
    label: "See the staff list",
    risk: "standard",
    today: "open",
    guards: ["GET /users"],
  },
  "people.manage": {
    module: "access",
    label: "Add staff, invite them, and choose their locations",
    risk: "sensitive",
    today: "admin",
    implies: ["people.view"],
    guards: [
      "POST /users; PATCH /users/:id",
      "POST /users/:id/invite",
      "POST /users/:id/memberships; DELETE /users/:id/memberships/:branchId",
    ],
  },
  "access.roles.manage": {
    module: "access",
    label: "Create and change roles",
    risk: "critical",
    today: "new",
    implies: ["people.view"],
    guards: ["(new) /access-roles"],
  },
  "access.assign": {
    module: "access",
    label: "Give people roles and overrides",
    risk: "critical",
    today: "new",
    implies: ["people.view"],
    guards: ["(new) /users/:id/access"],
  },
  "branches.all": {
    module: "access",
    label: "Work in every location without being rostered to it (B1)",
    risk: "critical",
    today: "admin",
    guards: ["BranchScopeGuard"],
  },
} as const satisfies Record<string, PermissionDef>;

export type Permission = keyof typeof PERMISSIONS;

export const ALL_PERMISSIONS = Object.keys(PERMISSIONS) as Permission[];

export function def(permission: Permission): PermissionDef {
  return PERMISSIONS[permission];
}

/** Every permission the safety floor carries. */
export const FLOOR: readonly Permission[] = ALL_PERMISSIONS.filter((p) => def(p).floor === true);

/**
 * What any signed-in member of a branch may reach with no permission at all —
 * the reads the shell needs to draw itself. Declared on a route as `@Member()`,
 * so "open to everyone" is a decision somebody wrote down rather than the
 * absence of a decorator.
 */
export const MEMBER_BASELINE: readonly string[] = [
  "GET /branches, /chairs, /branches/:id/hours, /block-reasons",
  "GET /clinic/branding",
  "GET /auth/me — who they are, and every permission they hold",
  "/auth/* — signing in, refreshing, changing your own password",
  "/tasks — your own tasks and your roles' unclaimed ones, and adding one (TK-D24)",
  "GET /task-types — the types offered when adding a task",
];

// ── Starter roles ────────────────────────────────────────────────────────────
//
// Content, not configuration: accepting them copies each into the clinic's own
// `access_roles` rows. `narrows` says, in plain words, what a default takes away
// from v1's permissive model, because that is what a clinic has to choose.

/**
 * `users.role` — what somebody is clinically, which alone decides who may be a
 * provider on an appointment. **Never what they may reach** (RB1). The same five
 * values as `ROLES` in this package's index; kept here so this file imports
 * nothing.
 */
export type Job = "ADMIN" | "DENTIST" | "HYGIENIST" | "ASSISTANT" | "FRONT_DESK";

export interface StarterRole {
  key: string;
  name: string;
  /** The matrix column header. */
  short: string;
  purpose: string;
  /**
   * New staff with this job are offered this role first. An offer, not a rule:
   * the admin may pick another, and the job keeps deciding who may be a
   * provider on an appointment whatever role they are given.
   */
  defaultFor: readonly Job[];
  /** Administrator only: always every permission, cannot be edited or emptied (A3). */
  locked?: true;
  permissions: readonly Permission[];
  /** What this default takes away from today's permissive v1, in plain words. */
  narrows?: string;
}

const CLINICAL: readonly Permission[] = [
  "home.view",
  "calendar.view",
  "appointments.book",
  "appointments.move",
  "appointments.status",
  "appointments.cancel",
  "waitlist.manage",
  "patients.view",
  "patients.create",
  "patients.edit",
  "patients.flags.manage",
  "clinical.chart.edit",
  "clinical.notes.write",
  "clinical.notes.hide",
  "clinical.imaging.upload",
  "clinical.imaging.delete",
  "procedures.view",
  "plans.edit",
  "staff.rota.edit.own",
  "staff.timeoff.request",
  "inventory.movement.record",
  "people.view",
];

export const STARTER_ROLES: readonly StarterRole[] = [
  {
    key: "administrator",
    name: "Administrator",
    short: "Admin",
    purpose: "Owns the practice's setup and decides who may do what.",
    defaultFor: [],
    locked: true,
    permissions: ALL_PERMISSIONS,
  },
  {
    key: "practice-manager",
    name: "Practice manager",
    short: "Manager",
    purpose:
      "Runs the practice day to day: diary, money, rota, stock, locations. No clinical writing, no access management.",
    defaultFor: ["ADMIN"],
    permissions: [
      "home.view",
      "appointments.book",
      "appointments.move",
      "appointments.status",
      "appointments.cancel",
      "appointments.validate",
      "calendar.roster",
      "calendar.across_locations",
      "waitlist.manage",
      "reminders.send",
      "patients.create",
      "patients.edit",
      // The booking form's tooth picker draws the chart (BT10).
      "clinical.chart.view",
      "plans.view",
      "procedures.manage",
      "billing.invoice.create",
      "billing.invoice.issue",
      "billing.invoice.void",
      "billing.price.override",
      "billing.payment.record",
      "billing.reports.view",
      "staff.rota.edit.any",
      "staff.rota.publish",
      "staff.timeoff.request",
      "staff.timeoff.decide",
      "hr.record.manage",
      "hr.compliance.manage",
      "hr.absence.record",
      "inventory.movement.record",
      "inventory.items.manage",
      "settings.locations.manage",
      "people.manage",
      "branches.all",
    ],
    narrows:
      "Cannot read clinical notes or imaging, or record work on the chart; cannot change roles or the clinic's own details.",
  },
  {
    key: "dentist",
    name: "Dentist",
    short: "Dentist",
    purpose:
      "Examines, treats and records. Books their own follow-ups. No access to the financial module.",
    defaultFor: ["DENTIST"],
    permissions: CLINICAL,
    narrows:
      "Loses billing entirely — invoices, payments, and the money on Home. Cannot edit colleagues' rota or decide their leave.",
  },
  {
    key: "hygienist",
    name: "Hygienist",
    short: "Hygienist",
    purpose:
      "Identical to Dentist by default — kept as its own role so a clinic can narrow it without touching dentists.",
    defaultFor: ["HYGIENIST"],
    permissions: CLINICAL,
    narrows: "As Dentist.",
  },
  {
    key: "dental-nurse",
    name: "Dental nurse",
    short: "Nurse",
    purpose:
      "Assists at the chair: seats patients, records notes and images, records materials used.",
    defaultFor: ["ASSISTANT"],
    permissions: [
      "home.view",
      "appointments.status",
      "patients.view",
      "clinical.chart.view",
      "clinical.notes.write",
      "clinical.imaging.upload",
      "procedures.view",
      "plans.view",
      "staff.rota.edit.own",
      "staff.timeoff.request",
      "inventory.movement.record",
      "people.view",
    ],
    narrows: "Cannot book, move or cancel; cannot change the chart; no billing.",
  },
  {
    key: "receptionist",
    name: "Receptionist",
    short: "Reception",
    purpose: "Runs the front desk: books, checks in, takes payments, handles the agent's bookings.",
    defaultFor: ["FRONT_DESK"],
    permissions: [
      "home.view",
      "appointments.book",
      "appointments.move",
      "appointments.status",
      "appointments.cancel",
      "appointments.validate",
      "calendar.across_locations",
      "waitlist.manage",
      "reminders.send",
      "patients.create",
      "patients.edit",
      // The booking form's tooth picker draws the chart (BT10).
      "clinical.chart.view",
      "procedures.view",
      "plans.view",
      "billing.invoice.create",
      "billing.invoice.issue",
      // R-D5 (2026-10-04): the front desk keeps voiding and changing a price,
      // as every member could before roles.
      "billing.invoice.void",
      "billing.price.override",
      "billing.payment.record",
      "staff.rota.edit.own",
      "staff.timeoff.request",
      "inventory.view",
      "people.view",
    ],
    narrows: "Cannot read clinical notes or imaging.",
  },
  {
    key: "finance-officer",
    name: "Finance officer",
    short: "Finance",
    purpose: "Owns the money: invoices, payments, voids, price overrides, reports, accounting.",
    defaultFor: [],
    permissions: [
      "home.view",
      "calendar.view",
      "patients.edit",
      "procedures.view",
      "plans.view",
      "billing.invoice.create",
      "billing.invoice.issue",
      "billing.invoice.void",
      "billing.price.override",
      "billing.payment.record",
      "billing.reports.view",
      "accounting.view",
      "accounting.expenses.view",
      "accounting.salaries.view",
      "accounting.manage",
      "accounting.salaries.manage",
      "hr.team.view",
      "staff.rota.edit.own",
      "staff.timeoff.request",
      "inventory.view",
      "people.view",
    ],
    narrows: "Cannot book or treat; no clinical record.",
  },
];

/**
 * **Migration only — never offered to a new clinic.** Every permission an
 * ordinary member holds today, generated from `today: "open"` rather than
 * listed by hand, so it is equal to v1's behaviour by construction. Existing
 * clinics move every non-admin onto it, and their admin moves people to real
 * roles when they choose. Administrators are mapped from `can_administer`.
 */
export const CURRENT_ACCESS_ROLE: StarterRole = {
  key: "current-access",
  name: "Staff (current access)",
  short: "Today",
  purpose:
    "Exactly what a signed-in member could do before roles existed. Retire it once everybody has a real role.",
  defaultFor: [],
  permissions: ALL_PERMISSIONS.filter((p) => def(p).today === "open"),
};

// ── The rule ─────────────────────────────────────────────────────────────────
//
// How a person's effective access is worked out — one pure function, so the
// rule can be read in one place and tested without a database.
//
//   effective = licensed ∩ ( floor ∪ closure( (∪ roles ∪ live grants) − needs(live revokes) ) )
//
// In words, in the order it runs:
//
//  1. **Roles add up.** Somebody holding Dentist and Finance officer holds both
//     sets. There is no role-level deny, so the order roles are listed in can
//     never change the answer.
//  2. **A live grant override adds one permission** to that person.
//  3. **A live revoke override removes one permission, and everything that
//     needs it.** Revoking "see invoices" also removes "record a payment",
//     because a payment cannot be taken against an invoice nobody can see. This
//     is the only subtraction in the model, and it happens at one level only.
//  4. **What remains brings its dependencies** (`implies` in the catalogue).
//  5. **The safety floor is added last**, so nothing can take it away.
//  6. **Modules the clinic's plan leaves out are removed**, whatever the roles
//     say — a subscription limit is not a permission and no role overrides it.
//
// An override whose `expiresAt` has passed is ignored rather than deleted, so
// nothing has to run at midnight and the history stays readable.

export type Effect = "grant" | "revoke";

export interface RoleAssignment {
  name: string;
  permissions: readonly Permission[];
}

/** One person's deviation from their roles. Rare, named, visible and dated. */
export interface Override {
  permission: Permission;
  effect: Effect;
  /** Required. "Manages the books" — never empty, never "per request". */
  reason: string;
  grantedBy: string;
  expiresAt: Date | null;
}

export interface AccessInput {
  roles: readonly RoleAssignment[];
  overrides: readonly Override[];
  /** Non-core modules the clinic's plan includes. Omitted means every module. */
  licensed?: ReadonlySet<ModuleKey>;
  now: Date;
}

export interface Access {
  readonly permissions: ReadonlySet<Permission>;
  has(permission: Permission): boolean;
  /** One sentence an administrator can read: why yes, or why no. */
  why(permission: Permission): string;
}

const isLive = (override: Override, now: Date): boolean =>
  override.expiresAt === null || override.expiresAt.getTime() > now.getTime();

const CLOSURES = new Map<Permission, ReadonlySet<Permission>>();

/** Everything `permission` brings with it, itself included. */
export function closure(permission: Permission): ReadonlySet<Permission> {
  const cached = CLOSURES.get(permission);
  if (cached !== undefined) return cached;
  const seen = new Set<Permission>([permission]);
  const queue: Permission[] = [permission];
  while (queue.length > 0) {
    const next = queue.pop() as Permission;
    for (const implied of def(next).implies ?? []) {
      const key = implied as Permission;
      if (!seen.has(key)) {
        seen.add(key);
        queue.push(key);
      }
    }
  }
  CLOSURES.set(permission, seen);
  return seen;
}

const isCore = (module: ModuleKey): boolean => MODULES.find((m) => m.key === module)?.core === true;

const until = (date: Date | null): string =>
  date === null ? "" : `, until ${date.toISOString().slice(0, 10)}`;

export function resolve(input: AccessInput): Access {
  const grants = input.overrides.filter((e) => e.effect === "grant" && isLive(e, input.now));
  // Defensive: validateOverride refuses a revoke of the floor, so none should reach here.
  const revokes = input.overrides.filter(
    (e) => e.effect === "revoke" && isLive(e, input.now) && def(e.permission).floor !== true,
  );
  const revoked = new Set(revokes.map((e) => e.permission));

  const asked = new Set<Permission>([
    ...input.roles.flatMap((r) => r.permissions),
    ...grants.map((e) => e.permission),
  ]);

  // 3 — drop anything that needs a revoked permission.
  const kept = [...asked].filter((p) => ![...closure(p)].some((q) => revoked.has(q)));

  // 4 and 5 — dependencies, then the floor.
  const held = new Set<Permission>([...kept.flatMap((p) => [...closure(p)]), ...FLOOR]);

  // 6 — the plan.
  const unlicensed = (p: Permission): ModuleKey | undefined =>
    input.licensed === undefined
      ? undefined
      : [...closure(p)]
          .map((q) => def(q).module)
          .find((m) => !isCore(m) && !input.licensed?.has(m));
  for (const p of [...held]) if (unlicensed(p) !== undefined) held.delete(p);

  const why = (permission: Permission): string => {
    if (held.has(permission)) {
      const reasons: string[] = [];
      for (const role of input.roles)
        if (role.permissions.includes(permission)) reasons.push(`role ${role.name}`);
      for (const e of grants)
        if (e.permission === permission)
          reasons.push(`override "${e.reason}" (${e.grantedBy}${until(e.expiresAt)})`);
      const carriers = kept.filter((p) => p !== permission && closure(p).has(permission));
      if (reasons.length === 0 && carriers.length > 0) reasons.push(`comes with ${carriers[0]}`);
      if (reasons.length === 0 && def(permission).floor === true)
        reasons.push("the safety floor — every member has it");
      return `Yes — ${reasons.join("; ")}.`;
    }
    const module = unlicensed(permission);
    if (module !== undefined) {
      const label = MODULES.find((m) => m.key === module)?.label ?? module;
      return `No — the clinic's plan does not include ${label}.`;
    }
    const wouldHold = [...asked].some((p) => closure(p).has(permission));
    const revoke = wouldHold
      ? revokes.find((e) => closure(permission).has(e.permission))
      : undefined;
    if (revoke !== undefined) {
      const how =
        revoke.permission === permission
          ? "revoked"
          : `needs ${revoke.permission}, which is revoked`;
      return `No — ${how} by override "${revoke.reason}" (${revoke.grantedBy}${until(revoke.expiresAt)}).`;
    }
    return "No — none of their roles gives it, and no override grants it.";
  };

  return { permissions: held, has: (p) => held.has(p), why };
}

export interface OverrideRequest {
  actorId: string;
  targetUserId: string;
  override: Override;
  /** The target's roles, to warn when a grant changes nothing. */
  targetRoles: readonly RoleAssignment[];
  now: Date;
}

/**
 * The rules an override must pass before it is saved. `errors` refuse it;
 * `warnings` are shown and may be accepted.
 */
export function validateOverride(request: OverrideRequest): {
  errors: string[];
  warnings: string[];
} {
  const { override, now } = request;
  const permission = def(override.permission);
  const errors: string[] = [];
  const warnings: string[] = [];

  if (request.actorId === request.targetUserId)
    errors.push("Nobody changes their own access — another administrator has to.");
  if (permission.risk === "critical")
    errors.push(`${override.permission} reaches access itself, so only a role may carry it.`);
  if (override.effect === "revoke" && permission.floor === true)
    errors.push(`${override.permission} is the safety floor and cannot be revoked.`);
  if (override.reason.trim().length === 0) errors.push("An override needs a reason.");
  if (override.expiresAt !== null && override.expiresAt.getTime() <= now.getTime())
    errors.push("The expiry date has already passed.");

  const fromRoles = resolve({ roles: request.targetRoles, overrides: [], now });
  if (override.effect === "grant" && fromRoles.has(override.permission))
    warnings.push(
      `Already held — ${fromRoles.why(override.permission).replace(/^Yes — /, "")} No override is needed.`,
    );
  if (override.effect === "revoke" && !fromRoles.has(override.permission))
    warnings.push("Not held through any role, so revoking it changes nothing.");
  if (permission.risk === "sensitive" && override.effect === "grant" && override.expiresAt === null)
    warnings.push(
      "A sensitive permission granted with no end date. Consider an expiry, or a role.",
    );

  return { errors, warnings };
}

/**
 * The drift report: an override held by `threshold` people or more is a role
 * nobody has named yet. Shown to administrators, never acted on automatically.
 */
export function sharedOverrides(
  held: readonly { userId: string; permission: Permission; effect: Effect }[],
  threshold = 3,
): { permission: Permission; effect: Effect; people: number }[] {
  const counts = new Map<string, number>();
  for (const e of held)
    counts.set(`${e.effect}:${e.permission}`, (counts.get(`${e.effect}:${e.permission}`) ?? 0) + 1);
  return [...counts]
    .filter(([, people]) => people >= threshold)
    .map(([key, people]) => {
      const [effect, permission] = key.split(":") as [Effect, Permission];
      return { permission, effect, people };
    });
}

// ── Product helpers ──────────────────────────────────────────────────────────

const PERMISSION_KEYS: ReadonlySet<string> = new Set(ALL_PERMISSIONS);

/** Whether a stored key is one this release knows. An unknown key is ignored, never an error (RB8). */
export function isPermission(key: string): key is Permission {
  return PERMISSION_KEYS.has(key);
}

/** The starter role offered to a job by default, if any. */
export function starterRoleForJob(job: Job): StarterRole | undefined {
  return STARTER_ROLES.find((role) => role.locked !== true && role.defaultFor.includes(job));
}

/**
 * What a stored role holds. A `CURRENT_ACCESS` role is v1 by construction, so
 * its permissions come from the catalogue rather than from rows — a permission
 * added to the catalogue with `today: "open"` reaches it with no migration.
 */
export function storedRolePermissions(
  kind: AccessRoleKind,
  listed: readonly string[],
): Permission[] {
  if (kind === "CURRENT_ACCESS") return [...CURRENT_ACCESS_ROLE.permissions];
  return listed.filter(isPermission);
}

/** `access_roles.kind`: v1's built-in fallback, or a role the clinic owns (starter or its own). */
export type AccessRoleKind = "CURRENT_ACCESS" | "CUSTOM";

/**
 * The code on every 403 a missing permission causes, beside `permission` — the
 * key that was missing — so a screen can say what is needed without matching
 * the sentence, which changes whenever anybody rewords it.
 */
export const PERMISSION_REQUIRED = "PERMISSION_REQUIRED";

/** The sentence a refusal carries. Names the action in words a person uses. */
export function refusalMessage(permission: Permission): string {
  return `Your access does not include: ${def(permission).label}. An administrator can change that in Settings → Access.`;
}

/** The name the Administrator role is shown under. It is backed by `users.can_administer` (RB9). */
export const ADMINISTRATOR_ROLE_NAME = "Administrator";

// ── Wire shapes ──────────────────────────────────────────────────────────────

/**
 * Somebody holding a role at one location (R-D6). `isDefault` is a location
 * where nobody gave them a role, so they hold the one for their job.
 */
export interface RoleHolderView {
  userId: string;
  name: string;
  branchId: string;
  branchName: string;
  isDefault: boolean;
}

/** One role as Settings → Roles and access lists it. */
export interface AccessRoleView {
  id: string;
  name: string;
  description: string | null;
  kind: AccessRoleKind;
  starterKey: string | null;
  /** Jobs whose new members get this role. */
  defaultForJobs: Job[];
  /** Given to a new member whose job no role claims. Exactly one per clinic. */
  isFallback: boolean;
  /** What the role lists. Empty for CURRENT_ACCESS, whose set comes from the catalogue. */
  permissions: Permission[];
  /** Listed permissions plus everything they bring. */
  effective: Permission[];
  /** How many different people hold it, at any location. */
  holders: number;
  /** Who holds it and where — one row per person per location. */
  holderNames: RoleHolderView[];
}

export interface AccessOverrideView {
  id: string;
  userId: string;
  userName: string;
  permission: Permission;
  effect: Effect;
  reason: string;
  expiresAt: string | null;
  grantedByName: string;
  createdAt: string;
  live: boolean;
}

/** What somebody can do at one location, with the reason for each. */
export interface LocationAccessView {
  branchId: string;
  branchName: string;
  /** The roles they hold here — or, when `isDefault`, the one for their job. */
  roles: { id: string; name: string }[];
  isDefault: boolean;
  permissions: { permission: Permission; held: boolean; why: string }[];
}

/** One person's access, as the person page draws it (R-D6: per location). */
export interface PersonAccessView {
  userId: string;
  job: Job;
  isAdministrator: boolean;
  /** Deactivated people hold their roles still and can do nothing (only the safety floor). */
  isActive: boolean;
  /** Every open location they work at, in name order. */
  locations: LocationAccessView[];
  /** Exceptions are the person's, and apply at every location. */
  overrides: AccessOverrideView[];
}

/** Settings → Roles and access → Who can…: one action, everyone, every location. */
export interface WhoCanView {
  permission: Permission;
  branches: { id: string; name: string }[];
  people: {
    userId: string;
    name: string;
    job: Job;
    isAdministrator: boolean;
    /** One per branch above; `held` is null where they do not work. */
    at: { branchId: string; held: boolean | null; because: string }[];
  }[];
}
